import { useState } from "react";
import { Link } from "react-router-dom";
import { PageHeader } from "@/components/layout/PageHeader";
import { Icon } from "@/components/ui/Icon";
import { MetricCard } from "@/components/ui/MetricCard";
import { ProjectionLag } from "@/components/ui/ProjectionLag";
import { ErrorState } from "@/components/ui/States";
import {
  isoDate,
  lastDays,
  rangeDays,
  useDashboard,
  type DateRange,
  type OpenCount,
  type PeriodCount,
} from "@/lib/api/dashboard";
import { useProjectionStatus } from "@/lib/api/reports";
import { useList } from "@/lib/api/queries";
import { Podium } from "./Podium";
import { RoleLeaders } from "./RoleLeaders";
import { PeriodPicker } from "./PeriodPicker";
import { TaskTypes, type TypeRow } from "./TaskTypes";
import { WarehouseFill } from "./WarehouseFill";
import "./dashboard.css";

/**
 * Hujjat holatlari — «tayyor» ko'rsatkichlari uchun.
 *
 * Sanoq SERVERDA bajariladi (`/counts`): ro'yxatni tortib
 * qatorlarini sanash chegaraga urilganda yolg'on son berardi.
 */
const READY_FOR_CONTROL = ["PICKED", "INCOMPLETE"];
const READY_FOR_LOADING = ["STAGED"];

interface StatusCountRow {
  status: string;
  count: number;
}

/** Berilgan holatlardagi hujjatlar yig'indisi. */
function countOf(rows: StatusCountRow[] | undefined, statuses: string[]): number {
  return (rows ?? [])
    .filter((row) => statuses.includes(row.status))
    .reduce((total, row) => total + row.count, 0);
}

/**
 * Ochiq topshiriqlarni guruhlash.
 *
 * `CREATED` — hali hech kim olmagan. Qolgan uchtasi xodim
 * QO'LIDA: `RESERVED` (biriktirildi) va `ACCEPTED` (oldi, lekin
 * boshlamadi) ni «bo'sh» deb sanash dispetcherga xodimni erkin
 * ko'rsatardi va u ikkinchi ishni ham o'sha odamga berardi.
 */
const QUEUED = ["CREATED"];
const HELD = ["RESERVED", "ACCEPTED", "IN_PROGRESS"];

/**
 * Topshiriq olishi mumkin bo'lgan rollar.
 *
 * Administrator, rahbar, dispetcher va buyurtma menejeri
 * topshiriq BAJARMAYDI — ularni «bo'sh xodim» deb sanash
 * dispetcherga yo'q resursni ko'rsatardi.
 */
const FLOOR_ROLES = new Set([
  "receiver",
  "picker",
  "forklift_driver",
  "senior_staff",
  "controller",
  "inventory_counter",
  "shipper",
  "production_worker",
  "return_clerk",
]);

interface User {
  id: string;
  full_name: string;
  username: string;
  roles: string[];
  is_active: boolean;
}

/** `CREATED` → «navbatda», `IN_PROGRESS` → «jarayonda». */
function bucketOf(status: string): "queued" | "held" | "stalled" {
  if (QUEUED.includes(status)) return "queued";
  if (HELD.includes(status)) return "held";
  // Nomi tanilmagan holat ham shu yerga tushadi: uni «bajarilmoqda»
  // deb ko'rsatish haqiqiy ishni YASHIRARDI, «navbatda» deb
  // ko'rsatish esa dispetcherni uni taqsimlashga undardi.
  return "stalled";
}

export function Dashboard() {
  const [range, setRange] = useState<DateRange>(() => lastDays(1));
  const [compare, setCompare] = useState<DateRange | null>(null);

  const summary = useDashboard(range, compare);
  const status = useProjectionStatus();

  // Xodim katalogi reporting-service'da yo'q: u ismni ham,
  // umumiy sonni ham bilmaydi. Shuning uchun ro'yxat
  // identity-service'dan bir marta olinadi.
  const staff = useList<User>("identity", "/api/v1/users", { limit: 500 });

  // «Tayyor» ko'rsatkichlari hujjat servislaridan: reporting
  // buyurtma holatini bilmaydi (unda buyurtma proyeksiyasi yo'q).
  const pickingCounts = useList<StatusCountRow>("picking", "/api/v1/picking/orders/counts");
  const shippingCounts = useList<StatusCountRow>("shipping", "/api/v1/shipments/counts");

  const data = summary.data;
  const open = data?.open ?? [];
  const periodRows = data?.period ?? [];

  const queued = sumOpen(open, "queued");
  const held = sumOpen(open, "held");
  const stalled = sumOpen(open, "stalled");
  const created = periodRows.reduce((total, row) => total + row.created, 0);
  const completed = periodRows.reduce((total, row) => total + row.completed, 0);
  const cancelled = periodRows.reduce((total, row) => total + row.cancelled, 0);

  const comparePeriod = data?.compare?.period ?? [];
  const createdBefore = comparePeriod.reduce((total, row) => total + row.created, 0);
  const completedBefore = comparePeriod.reduce((total, row) => total + row.completed, 0);

  const readyForControl = countOf(pickingCounts.data?.items, READY_FOR_CONTROL);
  const readyForLoading = countOf(shippingCounts.data?.items, READY_FOR_LOADING);

  const periodHint = periodLabel(range);

  const users = staff.data?.items ?? [];
  const names = new Map(
    users.map((user) => [user.id, user.full_name || user.username] as const),
  );
  const floor = users.filter(
    (user) => user.is_active && user.roles.some((role) => FLOOR_ROLES.has(role)),
  );
  const busyIDs = new Set(data?.busy_employee_ids ?? []);
  // Band xodim ro'yxatdan TOPILMASLIGI mumkin (rol o'zgargan,
  // o'chirilgan): shunda ham u band va uni sanoqdan tushirib
  // qoldirish «bo'sh» sonini oshirib ko'rsatardi.
  const busy = busyIDs.size;
  const free = Math.max(floor.filter((user) => !busyIDs.has(user.id)).length, 0);
  const total = staff.data?.has_more ? `${floor.length}+` : String(floor.length);

  const types = buildTypeRows(open, periodRows);

  return (
    <>
      <PageHeader
        title="Boshqaruv paneli"
        hint="Sonlar hodisalardan quriladi va o'zi yangilanadi — qayta yuklash kerak emas."
        actions={
          <>
            <PeriodPicker
              range={range}
              onRange={setRange}
              compare={compare}
              onCompare={setCompare}
            />
            <ProjectionLag status={status.data?.items} />
          </>
        }
      />

      {summary.error && (
        <ErrorState error={summary.error} onRetry={() => void summary.refetch()} />
      )}

      <section className="metrics">
        <MetricCard
          label="Jami vazifa"
          value={created}
          hint={`${periodHint} yaratilgan topshiriqlar`}
          loading={summary.isLoading}
          icon="task"
          extra={<Delta now={created} before={createdBefore} show={compare !== null} />}
        />
        <MetricCard
          label="Bajarilgan"
          value={completed}
          hint={
            cancelled > 0
              ? `${periodHint} yakunlandi · ${cancelled} bekor qilindi`
              : `${periodHint} yakunlandi`
          }
          loading={summary.isLoading}
          accent={completed > 0 ? "success" : undefined}
          icon="check"
          extra={<Delta now={completed} before={completedBefore} show={compare !== null} />}
        />
        <MetricCard
          label="Bajarilmoqda"
          value={held}
          hint="Hozir xodim jarayonda"
          loading={summary.isLoading}
          icon="refresh"
        />
        <MetricCard
          label="Navbatda"
          value={queued}
          hint="Hozir hech kim olmagan"
          loading={summary.isLoading}
          accent={queued > 0 ? "warning" : undefined}
          icon="layers"
        />

        {/* «Tayyor» ko'rsatkichlari: ish zanjirining KEYINGI
            bo'g'ini kimni kutayotganini ko'rsatadi. */}
        <MetricCard
          label="Tekshirishga tayyor"
          value={readyForControl}
          hint="Terilgan, nazoratchini kutmoqda"
          loading={pickingCounts.isLoading}
          accent={readyForControl > 0 ? "warning" : undefined}
          icon="check"
        />
        <MetricCard
          label="Yuklanishga tayyor"
          value={readyForLoading}
          hint="Yig'ilgan, mashinani kutmoqda"
          loading={shippingCounts.isLoading}
          accent={readyForLoading > 0 ? "warning" : undefined}
          icon="warehouse"
        />

        {/* Xodimlar kartasi IKKI sonli: «12 xodim» o'zi hech narsa
            aytmaydi, dispetcherga kerak bo'lgani — yana kimga ish
            berish mumkinligi. */}
        <article className={`card-glass glass${free === 0 ? " dash-free-none" : ""}`}>
          <div className="metric-head">
            <span className="metric-label">Xodimlar</span>
            <Icon name="shield" size={16} className="metric-icon" />
          </div>
          {summary.isLoading || staff.isLoading ? (
            <div className="skeleton" style={{ height: 32, width: 92, marginTop: 8 }} />
          ) : (
            <div className="dash-split">
              <span className="dash-split-main num">{free}</span>
              <span className="dash-split-rest">
                bo'sh · {busy} band / {total} jami
              </span>
            </div>
          )}
          <div className="metric-hint">
            {staff.error
              ? "Xodimlar ro'yxati o'qilmadi"
              : "Faqat topshiriq bajaradigan rollar"}
          </div>
        </article>
      </section>

      <section className="section">
        <div className="section-title">
          <Icon name="task" size={14} />
          Topshiriqlar turi bo'yicha
          <span style={{ flex: 1 }} />
          <Link to="/tasks" className="btn btn-text btn-sm">
            Hisobot
            <Icon name="chevronRight" size={14} />
          </Link>
        </div>

        <TaskTypes rows={types} loading={summary.isLoading} />

        {stalled > 0 && (
          <p className="field-hint">
            {stalled} topshiriq to'xtab turadi — inventarizatsiya, bog'liqlik yoki
            tafovut kutilmoqda. Blokda ular sariq belgi bilan ko'rsatilgan.
          </p>
        )}
      </section>

      {/* Ombor to'ldirilishi DAVRGA bog'liq emas: bu hozirgi
          qoldiq holati, tarix emas. Shuning uchun davr tanlagich
          bu bo'limni o'zgartirmaydi. */}
      <section className="section">
        <div className="section-title">
          <Icon name="stock" size={14} />
          Ombor to'ldirilishi
          <span style={{ flex: 1 }} />
          <Link to="/stock" className="btn btn-text btn-sm">
            Qoldiq
            <Icon name="chevronRight" size={14} />
          </Link>
        </div>

        <WarehouseFill />
      </section>

      <section className="section">
        <div className="section-title">
          <Icon name="report" size={14} />
          Eng natijali xodimlar
          <span style={{ flex: 1 }} />
          <Link to="/reports" className="btn btn-text btn-sm">
            KPI
            <Icon name="chevronRight" size={14} />
          </Link>
        </div>

        <div className="podium-wrap">
          <Podium
            rows={data?.podium ?? []}
            names={names}
            loading={summary.isLoading || staff.isLoading}
          />
          <p className="field-hint">
            {periodHint.charAt(0).toUpperCase() + periodHint.slice(1)} yakunlangan
            topshiriqlar soni bo'yicha.
          </p>
        </div>

        {/* Podium odatda BITTA kasbdan iborat bo'lib qoladi:
            terish topshiriqlari qolganlaridan ko'p va teruvchilar
            doim tepada turadi. Shuning uchun har bir kasb O'Z
            ichida ham taqqoslanadi. */}
        <div className="leaders-block">
          <h3 className="fill-title">
            <Icon name="shield" size={14} />
            Kasb bo'yicha eng yaxshilar
          </h3>
          <RoleLeaders
            rows={data?.leaders ?? []}
            names={names}
            loading={summary.isLoading || staff.isLoading}
          />
        </div>
      </section>
    </>
  );
}

/**
 * Davr nomi — izohlarda ishlatiladi.
 *
 * Tayyor oraliqlar uchun odam tili («bugun»), ixtiyoriy sanalar
 * uchun esa sanalarning o'zi: «oxirgi 23 kunda» degan ibora
 * tanlangan oraliqni aniq ko'rsatmasdi.
 */
function periodLabel(range: DateRange): string {
  const today = isoDate(new Date());
  const days = rangeDays(range);
  if (range.to === today) {
    if (days === 1) return "bugun";
    return `oxirgi ${days} kunda`;
  }
  if (days === 1) return range.from;
  return `${range.from} — ${range.to}`;
}

/**
 * Solishtirish farqi.
 *
 * Rang faqat YO'NALISHNI bildiradi, baho bermaydi: kam topshiriq
 * yomon degani emas (kam buyurtma bo'lgan kun ham shunday
 * ko'rinadi), shuning uchun pasayish qizil emas.
 */
function Delta({ now, before, show }: { now: number; before: number; show: boolean }) {
  if (!show) return null;
  // Oldingi davrda nol bo'lsa foiz CHEKSIZ bo'lardi — shunda
  // faqat farqning o'zi ko'rsatiladi.
  const diff = now - before;
  if (diff === 0) {
    return <span className="metric-delta">±0</span>;
  }
  const percent = before > 0 ? ` (${Math.round((diff / before) * 100)}%)` : "";
  return (
    <span className={`metric-delta${diff > 0 ? " up" : " down"}`}>
      {diff > 0 ? "+" : ""}
      {diff}
      {percent}
    </span>
  );
}

function sumOpen(
  open: Array<{ status: string; count: number }>,
  bucket: "queued" | "held" | "stalled",
): number {
  return open
    .filter((item) => bucketOf(item.status) === bucket)
    .reduce((total, item) => total + item.count, 0);
}

/**
 * Tur kesimini yig'adi.
 *
 * Ikki manba birlashtiriladi: ochiq topshiriqlar (davrdan
 * mustaqil) va davr ichida yakunlanganlar. Tur faqat bitta
 * manbada bo'lishi mumkin — masalan bugun hammasi bajarilgan tur
 * ochiq kesimda yo'q, lekin uni ro'yxatdan tushirib qoldirish
 * bajarilgan ishni yashirardi.
 */
function buildTypeRows(open: OpenCount[], period: PeriodCount[]): TypeRow[] {
  const rows = new Map<string, TypeRow>();
  // Miqdorlar MATN sifatida keladi (yaxlitlash xatosi uchun),
  // shuning uchun yig'indi alohida sonlar jadvalida to'planadi va
  // faqat oxirida matnga qaytariladi.
  const sums = new Map<string, { openQty: number; doneQty: number }>();

  const ensure = (taskType: string): TypeRow => {
    let row = rows.get(taskType);
    if (!row) {
      row = {
        task_type: taskType, queued: 0, held: 0, stalled: 0, completed: 0,
        openLines: 0, openQty: "0", doneQty: "0",
      };
      rows.set(taskType, row);
      sums.set(taskType, { openQty: 0, doneQty: 0 });
    }
    return row;
  };

  for (const item of open) {
    const row = ensure(item.task_type);
    row[bucketOf(item.status)] += item.count;
    row.openLines += item.lines;
    sums.get(item.task_type)!.openQty += Number(item.planned_qty || 0);
  }
  for (const item of period) {
    ensure(item.task_type).completed += item.completed;
    sums.get(item.task_type)!.doneQty += Number(item.actual_qty || 0);
  }
  for (const [taskType, total] of sums) {
    const row = rows.get(taskType)!;
    row.openQty = String(total.openQty);
    row.doneQty = String(total.doneQty);
  }

  // Tartib: eng band tur birinchi — dispetcher ko'zi birinchi
  // navbatda shu yerga tushadi.
  return [...rows.values()].sort(
    (left, right) =>
      right.queued + right.held + right.stalled -
        (left.queued + left.held + left.stalled) ||
      right.completed - left.completed ||
      left.task_type.localeCompare(right.task_type),
  );
}
